import { Logger } from "../utils/logger";

export class AppAd extends HTMLElement {
    logLabel: string;
    hardCodedSlotId: boolean = true;

    constructor({
        logLabel,
        hardCodedSlotId = true,
    }: {
        logLabel: string;
        hardCodedSlotId?: boolean;
    }) {
        super();
        this.logLabel = logLabel;
        this.hardCodedSlotId = hardCodedSlotId ?? true;
    }

    connectedCallback() {
        const windowWidth = window.innerWidth;
        if (this.showWhenWidth === null || windowWidth >= this.showWhenWidth) {
            this.innerHTML = this._buildHtml();
            this._insertAd();
            this._observeAdFill();
        } else {
            this.style.display = 'none';
        }
    }

    _buildHtml(): string {
        const slotId = this.slotId;
        if (this.mock) {
            let info: string[] = [`Mock ${this.logLabel}`];
            if (slotId) {
                info.push(` - Slot: ${slotId}`);
            }
            return `
                <div class="adsbygoogle with-border"
                    style="text-wrap: wrap;">
                    ${info.join(' - ')}
                </div>
            `;
        }
        if (!this.hardCodedSlotId && !slotId) {
            Logger.warn(this.logLabel, "Ad slotId is missing. Ad will not render.");
            return `
                <div class="adsbygoogle with-border"
                    style="text-wrap: wrap;">
                    Missing Ad Slot ID - ${this.logLabel}
                </div>
            `;
        }
        return this.buildHtml();
    }

    /** Override this in subclasses */
    buildHtml(): string {
        return `${this.logLabel} - buildHtml Not implemented`;
    }

    async _insertAd(): Promise<void> {
        if (this.mock) {
            Logger.info(this.logLabel, "Mock mode enabled. Ad will not load.");
            return;
        }

        let retries = 5;
        while (!(window as any).adsbygoogle && retries > 0) {
            Logger.warn(this.logLabel, `AdSense library not loaded, retrying... (${6 - retries}/5)`);
            await new Promise((resolve) => setTimeout(resolve, 1000));
            retries--;
        }

        if ((window as any).adsbygoogle) {
            try {
                (window as any).adsbygoogle.push({});
                Logger.info(this.logLabel, "AdSense ad successfully pushed.");
            } catch (e) {
                Logger.error(this.logLabel, `AdSense error: ${e}`);
            }
        } else {
            Logger.error(this.logLabel, "Failed to load AdSense library after 3 retries. Ad will not be displayed.");
        }
    }

    get slotId(): string | undefined {
        return this.getAttribute('slot') ?? undefined;
    }

    get lazyLoad(): boolean {
        return this.hasAttribute('lazyLoad') && this.getAttribute('lazyLoad') === 'true';
    }

    get mock(): boolean {
        return this.hasAttribute('mock') && this.getAttribute('mock') === 'true';
    }

    get showWhenWidth(): number | null {
        return this.hasAttribute('showWhenWidth') ? parseInt(this.getAttribute('showWhenWidth')!, 10) : null;
    }

    _observeAdFill() {
        const adSlot = this.querySelector('ins.adsbygoogle');
        if (!adSlot) return;

        const observer = new MutationObserver(() => {
            const adContent = adSlot.querySelector('iframe');
            if (!adContent) {
                Logger.info(this.logLabel, 'Ad was not filled. Hiding component.');
                this.style.display = 'none';
                observer.disconnect();
            } else {
                Logger.info(this.logLabel, 'Ad successfully filled.');
                observer.disconnect();
            }
        });

        observer.observe(adSlot, { childList: true, subtree: true });
    }
}
